import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchBarExams,
  fetchBarExamDraft,
  fetchMyBarExamSubmission,
  saveBarExamDraft,
  submitBarExamAnswers,
  updateBarExamSubmission,
} from "../api/barExam";
import { hasAuthenticatedSession } from "../api";

const SUBJECTS = [
  { code: "public_law", label: "공법", questionCount: 40 },
  { code: "criminal_law", label: "형사법", questionCount: 40 },
  { code: "civil_law", label: "민사법", questionCount: 70 },
];

const getStoredAnswersKey = (submissionId) =>
  `bar_exam_submission_answers_${submissionId}`;
const getLegacyDraftKey = (examId) => `bar_exam_draft_${examId}`;
const getDraftTokenKey = (examId) => `bar_exam_draft_token_${examId}`;

const normalizeSavedAnswers = (savedAnswers) => {
  const nextAnswers = {};

  savedAnswers.forEach((answer) => {
    nextAnswers[`${answer.subject_code}-${answer.number}`] =
      answer.selected_choice ?? null;
  });

  return nextAnswers;
};

const BarExamAnswer = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [exam, setExam] = useState(null);
  const [mySubmission, setMySubmission] = useState(null);
  const [nickname, setNickname] = useState("1");
  const [school, setSchool] = useState("1");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeSubjectIndex, setActiveSubjectIndex] = useState(0);
  const [showAnswerErrors, setShowAnswerErrors] = useState(false);
  const [hasSavedAnswers, setHasSavedAnswers] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle");
  const answerInputRefs = useRef({});
  const draftTokenRef = useRef(null);
  const lastSavedDraftRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const draftSavePromiseRef = useRef(Promise.resolve());

  const applyMySubmission = useCallback((data) => {
    setMySubmission(data);

    const submission = data?.submission;
    if (!submission) {
      setAnswers({});
      setHasSavedAnswers(false);
      return;
    }

    setNickname(submission.nickname || localStorage.getItem("nickname") || "");

    if (submission.school) {
      setSchool(submission.school);
    }

    let savedAnswers = submission.answers || data.answers;

    if (!Array.isArray(savedAnswers) && submission.id) {
      try {
        const storedAnswers = localStorage.getItem(
          getStoredAnswersKey(submission.id),
        );
        savedAnswers = storedAnswers ? JSON.parse(storedAnswers) : null;
      } catch (error) {
        console.error("Error loading stored bar exam answers:", error);
      }
    }

    if (Array.isArray(savedAnswers)) {
      setAnswers(normalizeSavedAnswers(savedAnswers));
      setHasSavedAnswers(true);
    } else {
      setHasSavedAnswers(false);
    }
  }, []);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const [examData, authenticated] = await Promise.all([
          fetchBarExams(),
          hasAuthenticatedSession(),
        ]);
        const exams = Array.isArray(examData)
          ? examData
          : examData?.results || [];
        const selectedExam = exams.find(
          (item) => String(item.id) === String(examId),
        );

        if (!selectedExam) {
          throw new Error("Exam not found");
        }

        setExam(selectedExam);
        setIsAuthenticated(authenticated);
        sessionStorage.setItem("last_bar_exam_id", selectedExam.id);

        const submissionData = authenticated
          ? await fetchMyBarExamSubmission(examId)
          : { exists: false, submission: null };
        applyMySubmission(submissionData);

        if (!submissionData?.exists) {
          let serverDraft = null;
          try {
            draftTokenRef.current = localStorage.getItem(
              getDraftTokenKey(examId),
            );
            const draftResponse = await fetchBarExamDraft(examId, {
              authenticated,
              draftToken: draftTokenRef.current,
            });

            if (draftResponse?.exists) {
              serverDraft = draftResponse.draft;
              setAnswers(serverDraft.answers || {});
              setNickname(serverDraft.nickname || "1");
              setSchool(serverDraft.school || "1");
              lastSavedDraftRef.current = JSON.stringify({
                answers: serverDraft.answers || {},
                nickname: serverDraft.nickname || "1",
                school: serverDraft.school || "1",
              });
              setDraftSaveStatus("saved");
            }

            if (authenticated && draftResponse?.owner === "account") {
              localStorage.removeItem(getDraftTokenKey(examId));
              draftTokenRef.current = null;
            }
          } catch (draftError) {
            console.error("Error loading server bar exam draft:", draftError);
            setDraftSaveStatus("error");
          }

          if (!serverDraft) {
            try {
              const storedDraft = localStorage.getItem(
                getLegacyDraftKey(examId),
              );
              if (storedDraft) {
                const legacyDraft = JSON.parse(storedDraft);
                setAnswers(legacyDraft.answers || {});
                setNickname(legacyDraft.nickname || "1");
                setSchool(legacyDraft.school || "1");
                // 기존 브라우저 초안은 자동 저장 효과에서 서버로 1회 이전한다.
                lastSavedDraftRef.current = null;
              } else {
                lastSavedDraftRef.current = JSON.stringify({
                  answers: {},
                  nickname: "1",
                  school: "1",
                });
              }
            } catch (draftError) {
              console.error("Error loading legacy bar exam draft:", draftError);
            }
          }
        }
        setDraftReady(true);
      } catch (error) {
        console.error("Error fetching bar exam:", error);
        setError(
          error.data?.detail || "시험 또는 제출 정보를 불러오지 못했습니다.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [applyMySubmission, examId]);

  const persistDraft = useCallback(
    (draft) => {
      setDraftSaveStatus("saving");
      const saveRequest = async () => {
        const result = await saveBarExamDraft(examId, draft, {
          authenticated: isAuthenticated,
          draftToken: draftTokenRef.current,
        });

        if (result?.draft_token) {
          draftTokenRef.current = result.draft_token;
          localStorage.setItem(getDraftTokenKey(examId), result.draft_token);
        } else if (isAuthenticated && result?.owner === "account") {
          draftTokenRef.current = null;
          localStorage.removeItem(getDraftTokenKey(examId));
        }

        localStorage.removeItem(getLegacyDraftKey(examId));
        lastSavedDraftRef.current = JSON.stringify(draft);
        setDraftSaveStatus("saved");
        return result;
      };

      draftSavePromiseRef.current = draftSavePromiseRef.current
        .catch(() => undefined)
        .then(saveRequest);
      return draftSavePromiseRef.current;
    },
    [examId, isAuthenticated],
  );

  useEffect(() => {
    if (!draftReady || !exam || mySubmission?.exists) return undefined;

    const draft = { answers, nickname, school };
    if (JSON.stringify(draft) === lastSavedDraftRef.current) return undefined;

    setDraftSaveStatus("saving");
    draftSaveTimerRef.current = window.setTimeout(() => {
      persistDraft(draft).catch((draftError) => {
        console.error("Error saving server bar exam draft:", draftError);
        setDraftSaveStatus("error");
      });
    }, 700);

    return () => window.clearTimeout(draftSaveTimerRef.current);
  }, [
    answers,
    draftReady,
    exam,
    mySubmission?.exists,
    nickname,
    persistDraft,
    school,
  ]);

  const getAnswerKey = (subjectCode, number) => `${subjectCode}-${number}`;

  const submission = mySubmission?.submission;
  const isEditMode = Boolean(mySubmission?.exists && submission?.can_edit);
  const isLocked = Boolean(mySubmission?.exists && !submission?.can_edit);

  const handleAnswerInput = (subjectCode, number, questionCount, value) => {
    const answerKey = getAnswerKey(subjectCode, number);
    const choice = value.replace(/[^1-5]/g, "").slice(-1);

    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [answerKey]: choice ? Number(choice) : null,
    }));

    if (choice && number < questionCount) {
      const nextAnswerKey = getAnswerKey(subjectCode, number + 1);
      answerInputRefs.current[nextAnswerKey]?.focus();
    }
  };

  const handleSubmit = async () => {
    if (isLocked) {
      setError("이미 제출한 답안이며 수정 가능 횟수를 모두 사용했습니다.");
      return;
    }

    if (!isAuthenticated) {
      window.clearTimeout(draftSaveTimerRef.current);
      try {
        await persistDraft({ answers, nickname, school });
      } catch (draftError) {
        console.error("Error saving draft before login:", draftError);
        setDraftSaveStatus("error");
        setError(
          "서버에 임시저장하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해주세요.",
        );
        return;
      }
      navigate("/login", {
        state: {
          returnTo: `/bar-exam/${exam.id}/answer`,
        },
      });
      return;
    }

    const firstBlankAnswer = SUBJECTS.flatMap((subject, subjectIndex) =>
      Array.from({ length: subject.questionCount }, (_, index) => ({
        subject,
        subjectIndex,
        number: index + 1,
      })),
    ).find(
      ({ subject, number }) =>
        answers[getAnswerKey(subject.code, number)] === null ||
        answers[getAnswerKey(subject.code, number)] === undefined,
    );

    if (firstBlankAnswer) {
      const { subject, subjectIndex, number } = firstBlankAnswer;
      const answerKey = getAnswerKey(subject.code, number);

      setShowAnswerErrors(true);
      setError(
        `모든 문항의 답안 칸을 채워주세요. (${subject.label} ${number}번부터 확인해주세요.)`,
      );
      setActiveSubjectIndex(subjectIndex);
      window.setTimeout(() => answerInputRefs.current[answerKey]?.focus(), 0);
      return;
    }

    setShowAnswerErrors(false);

    const submissionAnswers = SUBJECTS.flatMap((subject) =>
      Array.from({ length: subject.questionCount }, (_, index) => {
        const number = index + 1;

        return {
          subject_code: subject.code,
          number,
          selected_choice: answers[getAnswerKey(subject.code, number)],
        };
      }),
    );

    const createPayload = {
      exam_id: exam.id,
      nickname: nickname.trim(),
      school: school.trim(),
      answers: submissionAnswers,
    };

    setError(null);
    setSubmitting(true);

    try {
      if (!isEditMode) {
        window.clearTimeout(draftSaveTimerRef.current);
        await persistDraft({ answers, nickname, school });
      }
      const result = isEditMode
        ? await updateBarExamSubmission(submission.id, {
            answers: submissionAnswers,
          })
        : await submitBarExamAnswers(createPayload, draftTokenRef.current);
      const savedSubmissionId = result.submission?.id || submission?.id;

      if (savedSubmissionId) {
        localStorage.setItem(
          getStoredAnswersKey(savedSubmissionId),
          JSON.stringify(submissionAnswers),
        );
      }
      localStorage.removeItem(getLegacyDraftKey(exam.id));
      localStorage.removeItem(getDraftTokenKey(exam.id));
      draftTokenRef.current = null;

      navigate("/bar-exam/result", {
        state: { result, examId: exam.id },
      });
    } catch (error) {
      console.error("Error submitting bar exam answers:", error);
      const message =
        error.data?.detail ||
        (isEditMode
          ? "답안을 수정하지 못했습니다. 잠시 후 다시 시도해주세요."
          : "답안을 제출하지 못했습니다. 잠시 후 다시 시도해주세요.");

      setError(message);
      toast({
        title: isEditMode ? "답안 수정 실패" : "답안 제출 실패",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      if (!isEditMode && error.status === 400 && error.data?.submission_id) {
        try {
          const refreshedSubmission = await fetchMyBarExamSubmission(exam.id);
          applyMySubmission(refreshedSubmission);
        } catch (refreshError) {
          console.error("Error refreshing bar exam submission:", refreshError);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
      >
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!exam) {
    return (
      <Flex
        align="center"
        justify="center"
        minH="100vh"
        bgGradient="linear(to-r, blue.50, blue.100)"
        p={4}
      >
        <Alert status="error" rounded="md" maxW="600px">
          <AlertIcon />
          {error}
        </Alert>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-r, blue.50, blue.100)"
      p={{ base: 4, md: 8 }}
    >
      <Box width="full" maxW="1000px">
        <Heading as="h1" mb="2" textAlign="center">
          {exam.title}
        </Heading>
        <Text textAlign="center" color="gray.600" mb="8">
          {exam.exam_year}년 · {exam.exam_round}회
        </Text>

        {!mySubmission?.exists && (
          <Alert status="info" rounded="md" mb="6">
            <AlertIcon />
            <Box>
              <Text>
                {isAuthenticated
                  ? "답안은 계정에 연결되어 서버에 임시저장됩니다. 제출 후 수정은 1회만 가능합니다."
                  : "로그인 없이 답안을 입력할 수 있습니다. 답안은 서버에 임시저장되며, 제출할 때만 로그인이 필요합니다."}
              </Text>
            </Box>
          </Alert>
        )}

        {isEditMode && (
          <Alert status="warning" rounded="md" mb="6">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">
                이미 제출한 답안이 있습니다. 수정은 1회만 가능합니다.
              </Text>
              <Text fontSize="sm">
                수정 후에는 더 이상 변경할 수 없습니다.
                {!hasSavedAnswers &&
                  " 기존 답안 상세가 응답에 없어 모든 답안을 다시 입력해야 합니다."}
              </Text>
            </Box>
          </Alert>
        )}

        {isLocked && (
          <Alert status="error" rounded="md" mb="6">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">
                이미 제출한 답안이며 수정 가능 횟수를 모두 사용했습니다.
              </Text>
              <Text fontSize="sm">
                수정 횟수 {submission.edit_count}/{submission.max_edit_count}
              </Text>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert status="error" rounded="md" mb="6">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Box p={{ base: 3, md: 6 }} rounded="md" shadow="md" bg="white">
          <Text color="gray.600" fontSize="sm" mb="4">
            각 문항에 1부터 5까지의 숫자만 입력할 수 있습니다. 숫자를 입력하면
            다음 문항으로 자동 이동합니다. 제출하려면 모든 답안 칸을 채워야
            합니다.
          </Text>
          {!mySubmission?.exists && draftSaveStatus !== "idle" && (
            <Text
              color={draftSaveStatus === "error" ? "red.600" : "gray.500"}
              fontSize="sm"
              mb="4"
              role="status"
            >
              {draftSaveStatus === "saving" && "서버에 임시저장 중..."}
              {draftSaveStatus === "saved" && "서버에 임시저장되었습니다."}
              {draftSaveStatus === "error" &&
                "서버 임시저장에 실패했습니다. 인터넷 연결을 확인해주세요."}
            </Text>
          )}

          <Tabs
            index={activeSubjectIndex}
            onChange={setActiveSubjectIndex}
            colorScheme="blue"
            variant="enclosed"
            isFitted
            isLazy
          >
            <TabList>
              {SUBJECTS.map((subject) => (
                <Tab key={subject.code}>
                  {subject.label} ({subject.questionCount}문제)
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {SUBJECTS.map((subject) => (
                <TabPanel key={subject.code} px={{ base: 0, md: 4 }}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
                    {Array.from(
                      { length: subject.questionCount / 5 },
                      (_, groupIndex) => {
                        const startNumber = groupIndex * 5 + 1;
                        const endNumber = startNumber + 4;

                        return (
                          <Box
                            key={startNumber}
                            p="4"
                            borderWidth="1px"
                            rounded="md"
                          >
                            <Text fontWeight="bold" mb="3">
                              {startNumber}~{endNumber}번
                            </Text>
                            <SimpleGrid columns={5} spacing="2">
                              {Array.from({ length: 5 }, (_, index) => {
                                const number = startNumber + index;
                                const answerKey = getAnswerKey(
                                  subject.code,
                                  number,
                                );

                                return (
                                  <FormControl key={number}>
                                    <FormLabel
                                      fontSize="xs"
                                      textAlign="center"
                                      mb="1"
                                    >
                                      {number}번
                                    </FormLabel>
                                    <Input
                                      ref={(element) => {
                                        answerInputRefs.current[answerKey] =
                                          element;
                                      }}
                                      value={answers[answerKey] ?? ""}
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[1-5]*"
                                      maxLength={1}
                                      isDisabled={isLocked}
                                      isInvalid={
                                        showAnswerErrors && !answers[answerKey]
                                      }
                                      textAlign="center"
                                      fontSize="lg"
                                      fontWeight="bold"
                                      px="1"
                                      aria-label={`${number}번 문제 답안`}
                                      onFocus={(event) =>
                                        event.currentTarget.select()
                                      }
                                      onChange={(event) =>
                                        handleAnswerInput(
                                          subject.code,
                                          number,
                                          subject.questionCount,
                                          event.target.value,
                                        )
                                      }
                                    />
                                  </FormControl>
                                );
                              })}
                            </SimpleGrid>
                          </Box>
                        );
                      },
                    )}
                  </SimpleGrid>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>

          {isLocked ? (
            <Flex gap="3" mt="6" direction={{ base: "column", sm: "row" }}>
              <Button size="lg" width="full" isDisabled>
                수정 제출 불가
              </Button>
              <Button
                colorScheme="blue"
                size="lg"
                width="full"
                onClick={() =>
                  navigate("/bar-exam/result", {
                    state: { result: mySubmission, examId: exam.id },
                  })
                }
              >
                내 결과 보기
              </Button>
            </Flex>
          ) : (
            <Button
              colorScheme={isEditMode ? "orange" : "blue"}
              size="lg"
              width="full"
              mt="6"
              isLoading={submitting}
              loadingText={isEditMode ? "수정 중" : "제출 중"}
              onClick={handleSubmit}
            >
              {isEditMode
                ? "수정 답안 제출하기"
                : isAuthenticated
                  ? "답안 제출하기"
                  : "로그인하고 답안 제출하기"}
            </Button>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default BarExamAnswer;
